/**
 * Worked-for block grouping
 *
 * Collapses consecutive tool/reasoning runs between answer text into
 * "Worked for n seconds" groups for the chat message renderer.
 */

import type { MessageRenderSegment, ToolTimelineEntry } from '@/types/annotation'
import type { RenderSegmentLike } from '@/lib/chat/normalize-render-segments'
import {
  resolveExclusiveTextRange,
  sortRenderSegments
} from '@/lib/chat/normalize-render-segments'

export type WorkedForReasoningItem = {
  kind: 'reasoning'
  id: string
  text: string
  durationMs?: number
  isLive?: boolean
}

export type WorkedForToolItem = {
  kind: 'tool'
  id: string
  toolId: string
  entry?: ToolTimelineEntry | null
  /** Fallback annotation payload when tool_timeline is missing. */
  activityAnnotation?: Record<string, unknown> | null
}

export type WorkedForItem = WorkedForReasoningItem | WorkedForToolItem

export type WorkedForBlock =
  | {
      type: 'work'
      id: string
      items: WorkedForItem[]
    }
  | {
      type: 'text'
      id: string
      start: number
      end: number
      content: string
    }

export function formatWorkedDuration(durationMs: number): string {
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000))
  if (totalSeconds < 60) {
    return `${totalSeconds} ${totalSeconds === 1 ? 'second' : 'seconds'}`
  }

  const totalMinutes = Math.round(totalSeconds / 60)
  if (totalMinutes < 60) {
    return `${totalMinutes} ${totalMinutes === 1 ? 'minute' : 'minutes'}`
  }

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (minutes === 0) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  }
  return `${hours}h ${minutes}m`
}

export function getWorkedForLabel(args: {
  durationMs: number
  isLive: boolean
}): string {
  const durationLabel = formatWorkedDuration(args.durationMs)
  return args.isLive ? `Working for ${durationLabel}` : `Worked for ${durationLabel}`
}

export function estimateGroupDurationMs(
  items: WorkedForItem[],
  nowMs: number
): number {
  const toolEntries = items
    .filter((item): item is WorkedForToolItem => item.kind === 'tool')
    .map(item => item.entry)
    .filter((entry): entry is ToolTimelineEntry => Boolean(entry))

  const startedAtValues = toolEntries
    .map(entry => entry.startedAt)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))

  const endedAtValues = toolEntries
    .map(entry =>
      typeof entry.endedAt === 'number' && Number.isFinite(entry.endedAt)
        ? entry.endedAt
        : entry.status === 'running' || entry.status === 'progress'
          ? nowMs
          : entry.startedAt
    )
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))

  const reasoningDurations = items
    .filter((item): item is WorkedForReasoningItem => item.kind === 'reasoning')
    .map(item => item.durationMs)
    .filter((value): value is number => typeof value === 'number' && value > 0)

  if (startedAtValues.length > 0) {
    const start = Math.min(...startedAtValues)
    const end = endedAtValues.length > 0 ? Math.max(...endedAtValues) : nowMs
    const toolSpan = Math.max(0, end - start)
    const reasoningSpan =
      reasoningDurations.length > 0
        ? reasoningDurations.reduce((sum, value) => sum + value, 0)
        : 0
    // Wall-clock from tools, but don't undercount a preceding thought-only stretch.
    return Math.max(1000, toolSpan, reasoningSpan)
  }

  if (reasoningDurations.length > 0) {
    return Math.max(1000, reasoningDurations.reduce((sum, value) => sum + value, 0))
  }

  return 1000
}

export function isLiveWorkGroup(
  items: WorkedForItem[],
  isGenerating: boolean
): boolean {
  if (!isGenerating) return false
  if (items.some(item => item.kind === 'reasoning' && item.isLive)) return true
  return items.some(
    item =>
      item.kind === 'tool' &&
      item.entry &&
      (item.entry.status === 'running' || item.entry.status === 'progress')
  )
}

export function buildWorkedForBlocks(args: {
  segments: MessageRenderSegment[]
  contentLength: number
  content: string
  toolTimelineById: Map<string, ToolTimelineEntry>
  resolveToolFallback?: (toolId: string) => {
    activityAnnotation?: Record<string, unknown> | null
  } | null
  isGenerating?: boolean
}): WorkedForBlock[] {
  const sorted = sortRenderSegments(args.segments as RenderSegmentLike[]) as MessageRenderSegment[]
  const blocks: WorkedForBlock[] = []
  let pendingWork: WorkedForItem[] = []
  let textCursor = 0
  let workGroupIndex = 0

  const flushWork = () => {
    if (pendingWork.length === 0) return
    workGroupIndex += 1
    blocks.push({
      type: 'work',
      id: `worked-for-${workGroupIndex}`,
      items: pendingWork
    })
    pendingWork = []
  }

  const lastWorkSegment = [...sorted]
    .reverse()
    .find(segment => segment.type === 'tool' || segment.type === 'reasoning')

  sorted.forEach((segment, segmentIndex) => {
    if (segment.type === 'reasoning') {
      if (!segment.text?.trim()) return
      pendingWork.push({
        kind: 'reasoning',
        id: segment.id,
        text: segment.text,
        durationMs: segment.durationMs,
        // Only the trailing work item can be live — a thought before later
        // tools (or before a later work bunch) must not keep an earlier group open.
        isLive: Boolean(
          args.isGenerating && lastWorkSegment?.id === segment.id
        )
      })
      return
    }

    if (segment.type === 'tool') {
      const entry = args.toolTimelineById.get(segment.toolId) ?? null
      const fallback = args.resolveToolFallback?.(segment.toolId) ?? null
      pendingWork.push({
        kind: 'tool',
        id: segment.id,
        toolId: segment.toolId,
        entry,
        activityAnnotation: fallback?.activityAnnotation ?? null
      })
      return
    }

    const { start, end, nextCursor } = resolveExclusiveTextRange(
      segment,
      segmentIndex,
      sorted as RenderSegmentLike[],
      args.contentLength,
      textCursor
    )
    textCursor = nextCursor
    const slice = args.content.slice(start, end)
    if (!slice || !slice.trim()) return

    flushWork()
    blocks.push({
      type: 'text',
      id: segment.id,
      start,
      end,
      content: slice
    })
  })

  flushWork()
  return blocks
}
